import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Trophy, Medal, Crown, Calendar, ArrowRight } from "@phosphor-icons/react";

const HERO_BG = "https://images.pexels.com/photos/6565248/pexels-photo-6565248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return iso; } };

export default function Champions() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/seasons");
        setSeasons(data.seasons);
        if (data.seasons.length) setOpenId(data.seasons[0].id);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C]/85 to-[#090A0C]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Archive</span>
          <h1 className="font-heading text-5xl sm:text-6xl uppercase font-black mt-3">Past Seasons</h1>
          <p className="text-white/60 mt-3 max-w-xl">Every closed season. Every top predictor. Forever on the wall.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {loading && <div className="text-white/50 uppercase tracking-widest text-sm">Loading…</div>}
        {!loading && seasons.length === 0 && (
          <div data-testid="champions-empty" className="border border-white/10 p-16 text-center">
            <Trophy size={48} weight="duotone" className="mx-auto text-white/30" />
            <h2 className="font-heading text-3xl uppercase font-black mt-6">No seasons closed yet</h2>
            <p className="text-white/60 mt-2 text-sm">When the first season wraps, top predictors will be listed here.</p>
            <Link
              data-testid="champions-empty-cta"
              to="/fixtures"
              className="mt-8 inline-flex items-center gap-2 bg-primary-yellow text-black px-6 py-3 uppercase tracking-[0.2em] text-xs font-bold hover:-translate-y-0.5 transition-transform"
            >
              Start Predicting <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        )}
        {seasons.map((s) => {
          const open = openId === s.id;
          const podium = (s.standings || []).slice(0, 3);
          return (
            <div key={s.id} data-testid={`season-${s.id}`} className="surface border border-white/10">
              <button
                onClick={() => setOpenId(open ? null : s.id)}
                data-testid={`season-toggle-${s.id}`}
                className="w-full grid grid-cols-12 gap-4 items-center px-6 py-6 text-left hover:bg-[#15171C] transition"
              >
                <div className="col-span-12 md:col-span-6">
                  <div className="flex items-center gap-3">
                    <Trophy size={20} weight="fill" className="text-primary-yellow" />
                    <h2 className="font-heading text-3xl uppercase font-black">{s.label}</h2>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-white/50">
                    <Calendar size={12} /> Closed {fmtDate(s.closed_at)} · {s.participants} predictors
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 text-left md:text-right">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Top Predictor</div>
                  <div className="font-heading text-2xl uppercase font-black truncate">{s.winner?.name || "—"}</div>
                  <div className="font-mono-num text-3xl font-black text-primary-yellow">{s.winner?.total_points} pts</div>
                </div>
              </button>

              {open && (
                <div className="border-t border-white/10">
                  {/* Podium */}
                  <div className="grid md:grid-cols-3 gap-px bg-white/10 border-b border-white/10">
                    {podium.map((p, i) => {
                      const Icon = i === 0 ? Trophy : Medal;
                      const tag = ["Gold", "Silver", "Bronze"][i];
                      return (
                        <div key={p.user_id} className={`surface p-6 ${i === 0 ? "glow-yellow border border-primary-yellow/30" : ""}`}>
                          <div className="flex items-start justify-between">
                            <Icon size={24} weight="fill" className={i === 0 ? "text-primary-yellow" : "text-white/50"} />
                            <span className="text-[10px] uppercase tracking-widest text-white/40">{tag}</span>
                          </div>
                          <div className="font-heading text-2xl uppercase font-black mt-4 truncate">{p.name}</div>
                          <div className="mt-1 text-[10px] uppercase tracking-widest text-white/40">{p.predictions} picks · {p.exact_scores} exact</div>
                          <div className="font-mono-num text-4xl font-black text-primary-yellow mt-3">{p.total_points}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Full standings */}
                  <div className="px-2 sm:px-4 py-2">
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[10px] uppercase tracking-widest text-white/40">
                      <div className="col-span-1">Rank</div>
                      <div className="col-span-5">Predictor</div>
                      <div className="col-span-2 text-right">Picks</div>
                      <div className="col-span-2 text-right">Exact</div>
                      <div className="col-span-2 text-right">Points</div>
                    </div>
                    {(s.standings || []).map((row) => (
                      <div key={row.user_id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center">
                        <div className="col-span-1 font-mono-num text-base font-bold text-white/80">#{row.rank}</div>
                        <div className="col-span-5 flex items-center gap-3">
                          <div className="w-7 h-7 surface-elevated border border-white/10 grid place-items-center font-heading font-black uppercase text-xs">{(row.name || "?")[0]}</div>
                          <span className="font-semibold uppercase tracking-wide text-sm">{row.name}</span>
                          {row.rank === 1 && <Crown size={12} weight="fill" className="text-primary-yellow" />}
                        </div>
                        <div className="col-span-2 text-right font-mono-num text-sm">{row.predictions}</div>
                        <div className="col-span-2 text-right font-mono-num text-sm text-primary-yellow">{row.exact_scores}</div>
                        <div className="col-span-2 text-right font-mono-num text-lg font-black text-primary-yellow">{row.total_points}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
